// Services/NotificationService.ts
import * as signalR from "@microsoft/signalr";

export interface TaskReminderDto {
  taskId: number;
  title: string;
  description: string;
  reminderTime: string;
  priority: string;
  category: string;
  isTaskFollowUpDatePassed: boolean;
  metadata: {
    leadName: string;
    projectName: string;
    followUpType: string;
    followUpDate: string;
    taskId: number;
    assignedUserId: string;
    leadId?: number;
  };
}

export interface NewLeadReminderDto {
  leadId: number;
  firsT_NAME: string;
  lasT_NAME: string;
  projectName?: string;
  createD_ON: string;
  priority?: string;
  assignedUserId?: number;
}

export interface Notification {
  id: number;
  title: string;
  description: string;
  time: string;
  isRead: boolean;
  priority: string;
  category?: string;
  icon: string;
  metadata?: any;
  isTaskFollowUpDatePassed?: boolean;
  leadId?: number;
}

export class NotificationService {
  private connection: signalR.HubConnection | null = null;
  private notifications: Notification[] = [];
  private listeners: ((notifications: Notification[]) => void)[] = [];
  private isStarting = false;
  private taskIdSet: Set<number> = new Set();

  constructor(private baseUrl: string) {
    this.initializeConnection();
  }

  private initializeConnection() {
    const userId = localStorage.getItem("UserId");
    if (!userId) {
      console.error("UserId not found in localStorage");
      return;
    }

    console.log("Initializing SignalR connection with UserId:", userId);

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.baseUrl}/notificationhub?userId=${userId}`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => {
          const token = localStorage.getItem("authToken") || "";
          console.log("Token for SignalR:", token ? "Token found" : "No token");
          return token;
        },
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    if (!this.connection) return;

    // ✅ Handle task reminder notifications
    this.connection.on("ReceiveTaskReminder", (taskReminder: TaskReminderDto) => {
      const taskId = taskReminder.metadata?.taskId || taskReminder.taskId;

      // ✅ Check duplicate EARLY before creating notification object
      if (taskId && this.taskIdSet.has(taskId)) {
        console.log(`Duplicate task notification ignored for taskId: ${taskId}`);
        return;
      }

      const notification: Notification = {
        id: taskId ?? Date.now(), // ✅ use taskId as stable id
        title: taskReminder.title,
        description: taskReminder.description,
        time: this.formatTime(taskReminder.reminderTime),
        isRead: false,
        priority: taskReminder.priority,
        category: taskReminder.category || "default",
        icon: this.getIconForCategory(taskReminder.category || "default"),
        metadata: taskReminder.metadata,
        isTaskFollowUpDatePassed: taskReminder.isTaskFollowUpDatePassed,
        leadId: taskReminder.metadata?.leadId,
      };

      console.log("New notification received:", notification);
      this.addNotification(notification, taskId);
      this.showBrowserNotification(notification);
    });

    // ✅ Handle new lead reminder notifications
    this.connection.on("ReceiveNewLeadReminder", (newLead: NewLeadReminderDto) => {
      console.log("ReceiveNewLeadReminder payload:", newLead);

      const currentUserId = localStorage.getItem("UserId") || "";

      // ✅ Check duplicate by leadId early
      if (newLead.leadId && this.taskIdSet.has(newLead.leadId)) {
        console.log(`Duplicate lead notification ignored for leadId: ${newLead.leadId}`);
        return;
      }

      const assignedId =
        newLead.assignedUserId !== undefined && newLead.assignedUserId !== null
          ? newLead.assignedUserId.toString()
          : currentUserId;

      const leadName = newLead?.firsT_NAME
        ? `${newLead.firsT_NAME}${newLead?.lasT_NAME ? " " + newLead.lasT_NAME : ""}`
        : "Unknown Lead";

      const createdOn = newLead.createD_ON ?? new Date().toISOString();

      const notification: Notification = {
        id: newLead.leadId ?? Date.now(), // ✅ use leadId as stable id
        title: "New Lead Reminder",
        description: `New lead assigned: ${leadName}`,
        time: this.formatTime(createdOn),
        isRead: false,
        priority: newLead.priority || "High",
        category: "New Lead",
        icon: this.getIconForCategory("New Lead"),
        metadata: {
          leadName,
          projectName: newLead.projectName || "",
          leadId: newLead.leadId,
          assignedUserId: assignedId,
        },
      };

      console.log("New lead reminder received:", notification);
      this.addNotification(notification, newLead.leadId);
      this.showBrowserNotification(notification);
    });

    this.connection.onclose((error) => {
      console.log("SignalR connection closed:", error);
      this.isStarting = false;
    });

    this.connection.onreconnecting((error) => {
      console.log("SignalR reconnecting:", error);
      this.isStarting = false;
    });

    this.connection.onreconnected((connectionId) => {
      console.log("SignalR reconnected:", connectionId);
      this.isStarting = false;
      this.joinUserGroup();
    });
  }

  public async startConnection(): Promise<void> {
    if (!this.connection) return;

    if (this.connection.state === signalR.HubConnectionState.Connected) {
      console.log("SignalR already connected");
      return;
    }

    if (
      this.connection.state === signalR.HubConnectionState.Connecting ||
      this.isStarting
    ) {
      console.log("SignalR connection already starting");
      return;
    }

    this.isStarting = true;

    try {
      await this.connection.start();
      console.log("SignalR Connected");
      this.isStarting = false;
      await this.joinUserGroup();
    } catch (err) {
      console.error("SignalR Connection Error: ", err);
      this.isStarting = false;
      if (this.connection.state === signalR.HubConnectionState.Disconnected) {
        setTimeout(() => this.startConnection(), 5000);
      }
    }
  }

  public async stopConnection(): Promise<void> {
    if (
      this.connection &&
      this.connection.state !== signalR.HubConnectionState.Disconnected
    ) {
      this.isStarting = false;
      await this.connection.stop();
    }
  }

  private async joinUserGroup(): Promise<void> {
    const userId = localStorage.getItem("UserId");
    if (
      this.connection &&
      userId &&
      this.connection.state === signalR.HubConnectionState.Connected
    ) {
      try {
        await this.connection.invoke("JoinUserGroup", userId);
        console.log(`Joined user group: User_${userId}`);
      } catch (err) {
        console.error("Error joining user group:", err);
      }
    }
  }

  private addNotification(notification: Notification, trackingId?: number) {
    // ✅ Layer 1: Check duplicate by trackingId (taskId or leadId)
    if (trackingId && this.taskIdSet.has(trackingId)) {
      console.log(`Duplicate notification blocked in addNotification: ${trackingId}`);
      return;
    }

    // ✅ Layer 2: Check duplicate by stable notification id
    const existsById = this.notifications.some((n) => n.id === notification.id);
    if (existsById) {
      console.log(`Duplicate notification blocked by id: ${notification.id}`);
      return;
    }

    // ✅ Register trackingId
    if (trackingId) {
      this.taskIdSet.add(trackingId);
    }

    this.notifications.unshift(notification);

    // Maintain maximum of 50 notifications
    if (this.notifications.length > 50) {
      const removedNotification = this.notifications.pop();
      if (removedNotification?.metadata?.taskId) {
        this.taskIdSet.delete(removedNotification.metadata.taskId);
      }
    }

    this.notifyListeners();
  }

  private showBrowserNotification(notification: Notification) {
    if (Notification.permission === "granted") {
      new Notification(notification.title, {
        body: notification.description,
        icon: "/favicon.png",
        badge: "/favicon.png",
      });
    }
  }

  private formatTime(dateTime: string): string {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) {
      return "Just now";
    }

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return "Yesterday";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  private getIconForCategory(category: string): string {
    switch (category.toLowerCase()) {
      case "task reminder": return "bi bi-alarm";
      case "new lead":
      case "lead": return "bi bi-person-plus";
      case "follow up": return "bi bi-telephone";
      case "meeting": return "bi bi-calendar-event";
      case "system": return "bi bi-gear";
      default: return "bi bi-bell";
    }
  }

  public async markAsRead(notificationId: number): Promise<void> {
    const notification = this.notifications.find((n) => n.id === notificationId);
    if (!notification) return;

    notification.isRead = true;
    this.notifyListeners();

    if (
      this.connection &&
      this.connection.state === signalR.HubConnectionState.Connected
    ) {
      try {
        const payload =
          notification.metadata?.taskId ??
          notification.metadata?.leadId ??
          notificationId;
        await this.connection.invoke("MarkNotificationAsRead", payload);
      } catch (err) {
        console.error("Error marking notification as read:", err);
      }
    }
  }

  public markAllAsRead(): void {
    this.notifications.forEach((n) => (n.isRead = true));
    this.notifyListeners();
  }

  public getNotifications(): Notification[] {
    return this.notifications;
  }

  public getUnreadCount(): number {
    return this.notifications.filter((n) => !n.isRead).length;
  }

  public subscribe(listener: (notifications: Notification[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners() {
    // ✅ spread array to force React to detect change and re-render
    this.listeners.forEach((listener) => listener([...this.notifications]));
  }

  public static async requestNotificationPermission(): Promise<boolean> {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return false;
  }

  public getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state ?? signalR.HubConnectionState.Disconnected;
  }

  public async restartConnection(): Promise<void> {
    await this.stopConnection();
    setTimeout(() => this.startConnection(), 1000);
  }

  public clearAllNotifications(): void {
    this.notifications = [];
    this.taskIdSet.clear();
    this.notifyListeners();
  }

  public removeNotificationByTaskId(taskId: number): void {
    this.notifications = this.notifications.filter(
      (n) => n.metadata?.taskId !== taskId
    );
    this.taskIdSet.delete(taskId);
    this.notifyListeners();
  }
}

// Singleton instance
let notificationService: NotificationService | null = null;

export const getNotificationService = (baseUrl?: string): NotificationService => {
  if (!notificationService && baseUrl) {
    notificationService = new NotificationService(baseUrl);
  }
  return notificationService!;
};